/**
 * GPU - Scene
 *
 * Manages image and video planes for a single route/page.
 * Handles texture loading, DOM slot binding, and bounds synchronization.
 *
 * Responsibilities:
 * - Load textures from the media store (populated by loader)
 * - Find DOM elements with .g__l class and bind planes to them
 * - Detect <video> elements inside DOM slots and create VideoTextures
 * - Keep plane bounds synchronized with DOM element positions
 * - Trigger reveal animations when planes scroll into view
 * - Provide hover effect controls per-plane
 *
 * Lifecycle:
 * 1. Constructor: Sets up scene and calls setup()
 * 2. setup(): Loads textures and creates planes from media store
 * 3. collectDomSlots(): Finds DOM elements to bind planes to
 * 4. initVideoPlanes(): Creates VideoTextures from DOM <video> elements
 * 5. updateBounds(): Called each frame to sync positions
 *
 * The scene is created once per route and cached for reuse.
 */

import { VideoTexture, createSampler } from "kido/gpu";
import { bounds, queryAll } from "kido/utils";

import { App, type RouteMediaSlot } from "@app/context";

import type GPU from "./index";

/**
 * Bounds for a plane in pixel coordinates
 */
interface PlaneBounds {
  /** X position (left edge) in CSS pixels */
  x: number;
  /** Y position (top edge) in CSS pixels */
  y: number;
  /** Width in CSS pixels */
  w: number;
  /** Height in CSS pixels */
  h: number;
  /** Z-index for depth sorting (higher = in front) */
  z: number;
}

/**
 * Represents a single textured plane in the scene.
 * Contains both rendering data and effect state.
 */
export interface ScenePlane {
  // ==================== Core Rendering ====================

  /** WebGPU texture to render */
  texture: GPUTexture | null | undefined;

  /** WebGPU sampler for texture sampling */
  sampler: GPUSampler | null | undefined;

  /** Texture aspect ratio (width / height) */
  aspectRatio: number;

  /** Base opacity (0-1) */
  opacity: number;

  /** Optional: Externally-driven opacity target for animation */
  opacityTarget?: number;

  /** Optional: Eased opacity value from Anima */
  opacityEased?: number;

  /** Pixel bounds from DOM element (null if not yet computed) */
  boundsPx: PlaneBounds | null;

  /** Optional identifier for matching planes across scenes */
  id?: string;

  /** VideoTexture instance for video planes (null for image planes) */
  videoTexture?: VideoTexture | null;

  // ==================== Border & Shadow ====================

  /** Corner radius in CSS pixels (0 = sharp corners) */
  borderRadius?: number;

  /** Shadow blur radius in CSS pixels */
  shadowBlur?: number;

  /** Shadow offset in CSS pixels [x, y] */
  shadowOffset?: [number, number];

  /** RGBA shadow color (e.g., [0, 0, 0, 0.5] for 50% black) */
  shadowColor?: [number, number, number, number];

  // ==================== Scroll Effect ====================

  /** Target scroll warp strength */
  strength?: number;

  /** Eased (current) scroll warp strength */
  strengthEased?: number;

  // ==================== Rotation ====================

  /** Rotation angles in radians [x, y, z] */
  rotation?: [number, number, number];

  // ==================== Hover Effects ====================

  /** Target hover tilt angle in radians */
  hoverTilt?: number;

  /** Eased (current) hover tilt angle */
  hoverTiltEased?: number;

  /** Target hover zoom amount (0-1) */
  hoverZoom?: number;

  /** Eased (current) hover zoom amount */
  hoverZoomEased?: number;
}

/**
 * Scene class for managing route-specific image planes.
 *
 * @example
 * const scene = new Scene("/about", gpu, "about");
 * // Scene auto-loads textures and finds DOM slots
 *
 * // Later, to set hover effect:
 * const index = scene.getIndexForSlot(domElement);
 * scene.setHoverTilt(index, 0.1);
 */
export default class Scene {
  // ==================== Identity ====================

  /** Route URL this scene represents */
  readonly url: string;

  /** Page identifier (e.g., "home", "about", "case-project") */
  readonly id: string | null | undefined;

  // ==================== State ====================

  /** Reference to parent GPU orchestrator */
  private readonly gpu: GPU;

  /** Array of planes to render */
  planes: ScenePlane[];

  /** DOM elements bound to planes (by index) */
  domSlots: Array<Element | null>;

  /** Media slots from loader, kept for type checking */
  private mediaSlots: RouteMediaSlot[];

  /** Resolves when all video planes have received their first frame */
  videoReady: Promise<void>;

  /** Resolver for videoReady promise */
  private _resolveVideoReady: (() => void) | null;

  // ==================== Shared Scene State ====================

  /** Saved planes array before injectProjectMedia (null = not injected) */
  private _basePlanes: ScenePlane[] | null;

  /** Saved media slots before injectProjectMedia */
  private _baseMediaSlots: RouteMediaSlot[] | null;

  /** Original cover plane ID before injectProjectMedia renamed it */
  private _baseCoverId: string | null;

  /**
   * Creates a new scene for the given route.
   *
   * @param url - Route URL (e.g., "/", "/about")
   * @param gpu - Parent GPU orchestrator
   * @param id - Page identifier for route config lookup
   */
  constructor(url: string, gpu: GPU, id: string | null | undefined) {
    this.url = url;
    this.gpu = gpu;
    this.planes = [];
    this.domSlots = [];
    this.mediaSlots = [];
    this.id = id;
    this._resolveVideoReady = null;
    this._basePlanes = null;
    this._baseMediaSlots = null;
    this._baseCoverId = null;
    this.videoReady = new Promise<void>((resolve) => {
      this._resolveVideoReady = resolve;
    });

    // Auto-initialize on construction
    this.setup();
  }

  /**
   * Loads textures from media store and creates planes.
   *
   * The media store is populated by loader.ts during preloading.
   * Structure: App.gpu.media[url].main = array of texture slots
   *
   * Each slot contains:
   * - attrib[0]: GPUTexture (null for video slots)
   * - sampler: GPUSampler (null for video slots)
   * - wh: Aspect ratio
   * - id: Optional identifier
   * - type: "image" | "video"
   */
  setup(): void {
    const g = App.gpu as GPU | null;

    // Get media for this route's URL
    const media = (
      g?.media as Record<string, Record<string, RouteMediaSlot[]>> | undefined
    )?.[this.url];

    if (!media) return;

    // Get the main stack of textures
    const stack = media.main;
    if (!Array.isArray(stack)) return;

    // ==================== Create Planes ====================
    this.planes = [];
    this.mediaSlots = stack;

    for (const slot of stack) {
      const plane = this._createPlane(slot);
      if (plane) this.planes.push(plane);
    }

    // Find DOM elements to bind planes to
    this.collectDomSlots();

    // Initialize video planes from DOM <video> elements
    this.initVideoPlanes();

    // Initial bounds calculation
    this.updateBounds();
  }

  /**
   * Creates a ScenePlane from a RouteMediaSlot.
   */
  private _createPlane(slot: RouteMediaSlot): ScenePlane | null {
    const tex = slot?.attrib?.[0];
    const sampler = slot?.sampler ?? null;
    const aspectRatio = slot?.wh || 1;

    const plane: ScenePlane = {
      texture: tex ?? null,
      sampler: sampler,
      aspectRatio,
      opacity: 1,
      boundsPx: null,
      strength: 0,
      strengthEased: 0,
      borderRadius: 8,
      hoverTilt: 0,
      hoverTiltEased: 0,
      hoverZoom: 0,
      hoverZoomEased: 0,
    };
    if (slot?.id) {
      plane.id = slot.id;
    }
    return plane;
  }

  /**
   * Updates planes whose textures were null at creation time.
   * Called after the loader finishes uploading remaining textures.
   * Returns true if any plane was updated.
   */
  syncTexturesFromSlots(): boolean {
    let updated = false;
    for (let i = 0; i < this.planes.length && i < this.mediaSlots.length; i++) {
      const plane = this.planes[i];
      const slot = this.mediaSlots[i];
      if (plane && slot && !plane.texture && slot.type !== "video" && slot.attrib?.[0]) {
        plane.texture = slot.attrib[0];
        plane.sampler = slot.sampler;
        if (slot.wh) plane.aspectRatio = slot.wh;
        updated = true;
      }
    }
    return updated;
  }

  /**
   * Finds DOM elements to bind planes to.
   *
   * Maps planes to DOM slots via `data-plane` attributes on `.g__l`
   * elements. Each plane's `id` is matched to the element whose
   * `data-plane` value equals that id, so DOM order doesn't matter.
   *
   * Falls back to per-route selector config if present, or plain
   * index-based ordering when no `data-plane` attributes exist.
   */
  collectDomSlots(): void {
    const root = document.getElementById("app") ?? document;

    // Check for per-route selector configuration
    const selCfg = App.data.gpu.selectors?.[this.url]?.main ?? null;
    const slots: Array<Element | null> = [];

    if (Array.isArray(selCfg) && selCfg.length) {
      // ==================== Custom Selectors ====================
      for (let i = 0; i < selCfg.length; i++) {
        const s = selCfg[i];
        try {
          slots[i] = s ? root.querySelector(s) : null;
        } catch {
          slots[i] = null;
        }
      }
    } else {
      // ==================== data-plane ID Mapping ====================
      const els = queryAll(root, ".g__l");

      // Build id→element map from data-plane attributes
      const byId = new Map<string, Element>();
      for (const el of els) {
        const id = (el as HTMLElement).getAttribute("data-plane");
        if (id) byId.set(id, el);
      }

      if (byId.size > 0 && this.planes.length > 0) {
        // Match each plane to its DOM slot by id
        for (let i = 0; i < this.planes.length; i++) {
          const p = this.planes[i];
          slots[i] = (p?.id ? byId.get(p.id) : null) ?? null;
        }
      } else {
        // Fallback: index-based (legacy / no data-plane in DOM)
        for (let i = 0; i < els.length; i++) {
          slots[i] = els[i] ?? null;
        }
      }
    }

    this.domSlots = slots;
  }

  /**
   * Initialize video planes from DOM <video> elements.
   *
   * For each plane backed by a type: "video" media slot, looks for a
   * <video> child inside the corresponding DOM slot (.g__l element).
   * Creates a VideoTexture using that DOM element directly and wires
   * up the frame callback to request GPU redraws.
   */
  private initVideoPlanes(): void {
    const device = App.gpu?.device ?? null;
    if (!device) {
      this._resolveVideoReady?.();
      return;
    }

    // Build lookup: slot id → media slot (for type checking)
    const slotById = new Map<string, RouteMediaSlot>();
    for (const slot of this.mediaSlots) {
      if (slot?.id) slotById.set(slot.id, slot);
    }

    let pendingFrames = 0;

    for (let i = 0; i < this.planes.length; i++) {
      const plane = this.planes[i];
      if (!plane) continue;

      // Find matching media slot by plane ID or by index
      const slot = (plane.id ? slotById.get(plane.id) : null) ?? this.mediaSlots[i];
      if (slot?.type !== "video") continue;

      const el = this.domSlots[i];
      if (!el) continue;

      // Find the <video> element inside the DOM slot
      const videoEl = el.querySelector("video");
      if (!videoEl) {
        console.warn(
          `[scene] Video slot "${slot.id}" has no <video> element in DOM`
        );
        continue;
      }

      // If the plane already has a VideoTexture (e.g., cover preserved across inject),
      // hand off gracefully: save playback position, tear down old VT but keep GPU texture
      // so the last frame persists visually, then let a new VT be created below.
      let seekTime: number | null = null;
      if (plane.videoTexture) {
        seekTime = plane.videoTexture.currentTime ?? null;
        plane.videoTexture.pause();
        plane.videoTexture.onFrame(null);
        plane.videoTexture = null;
        // Keep plane.texture — last uploaded frame stays visible (no flash)
        // Keep plane.opacity — don't hide during handoff
      } else {
        // New video plane — hide until first frame
        plane.opacity = 0;
      }
      pendingFrames++;

      // Create VideoTexture using the DOM element directly
      const vt = new VideoTexture(device, { element: videoEl });

      // Initialize the GPU texture from the video element
      vt.init()
        .then(() => {
          plane.aspectRatio = vt.aspectRatio;
          plane.videoTexture = vt;

          if (!plane.sampler) {
            plane.sampler = createSampler(device);
          }

          // Fall back to native <video> if GPU frame upload fails
          const fallbackToNative = () => {
            console.warn(`[scene] Video texture upload failed for "${slot.id}", falling back to native video`);
            videoEl.style.visibility = "visible";
            videoEl.style.opacity = "1";
            plane.opacity = 0;
          };

          vt.onError(fallbackToNative);

          // Assign texture and reveal plane on first real frame
          let firstFrame = true;
          vt.onFrame(() => {
            plane.texture = vt.texture;
            // Only auto-reveal if no external opacity control (opacityTarget) is set
            if (plane.opacityTarget === undefined) {
              plane.opacity = 1;
            }
            App.gpu?.requestRedraw?.();

            if (firstFrame) {
              firstFrame = false;
              // Frame upload works — clear the error fallback
              vt.onError(null);
              pendingFrames--;
              if (pendingFrames === 0) {
                this._resolveVideoReady?.();
              }
            }
          });

          // When videosPlaying is false, get a first frame then pause.
          // Keep the onFrame callback so setVideosPlaying(true) triggers redraws.
          if (!App.gpu?.videosPlaying) {
            vt.play();
            const pauseAfterFrame = () => {
              videoEl.pause();
            };
            videoEl.addEventListener("timeupdate", pauseAfterFrame, { once: true });
          } else if (seekTime !== null) {
            // For handoff: seek to saved position and wait for the seek to
            // settle before starting the frame loop.  This prevents a flash
            // where the first uploaded frame is from the autoplay start
            // position instead of the saved playback position.
            videoEl.currentTime = seekTime;
            const startPlayback = () => vt.play();
            if (videoEl.seeking) {
              videoEl.addEventListener("seeked", startPlayback, { once: true });
            } else {
              startPlayback();
            }
          } else {
            // Start the frame upload loop (video may already be playing via DOM autoplay)
            vt.play();
          }
        })
        .catch((e) => {
          console.warn(`[scene] Failed to init video texture "${slot.id}":`, e);
          // Show native video as fallback
          videoEl.style.visibility = "visible";
          videoEl.style.opacity = "1";
          plane.opacity = 0;
          pendingFrames--;
          if (pendingFrames === 0) {
            this._resolveVideoReady?.();
          }
        });
    }

    // If no video planes, resolve immediately
    if (pendingFrames === 0) {
      this._resolveVideoReady?.();
    }
  }

  /**
   * Reinitialize video planes after a route return.
   *
   * When navigating back to a cached scene, the DOM has been swapped
   * so the old DOM slots and VideoTexture references are stale.
   * This method re-collects DOM slots and recreates VideoTextures.
   */
  reinitVideoPlanes(): void {
    // Pause and detach old video textures (don't destroy — they're external)
    for (const plane of this.planes) {
      if (plane.videoTexture) {
        plane.videoTexture.pause();
        plane.videoTexture.onFrame(null);
        plane.videoTexture = null;
        plane.texture = null;
      }
    }

    // Re-collect DOM slots from the new DOM
    this.collectDomSlots();

    // Reset videoReady so consumers can await the new video init
    this.videoReady = new Promise<void>((resolve) => {
      this._resolveVideoReady = resolve;
    });

    // Recreate video textures from the new DOM elements
    this.initVideoPlanes();

    // Recalculate bounds
    this.updateBounds();
  }

  // ==================== Shared Scene: Project Media ====================

  /**
   * Injects a specific project's media planes into the shared scene.
   *
   * Called by ProjectPage.init() when entering /projects/{slug}.
   * The shared /projects scene normally holds only cover planes.
   * This method swaps the planes array to: [cover] + [project media].
   *
   * @param projectRouteUrl - Full route URL, e.g. "/projects/my-project"
   */
  injectProjectMedia(projectRouteUrl: string, planeId?: string): void {
    console.debug("[scene] injectProjectMedia", projectRouteUrl, planeId);

    // Save originals for restorePlanes()
    this._basePlanes = [...this.planes];
    this._baseMediaSlots = [...this.mediaSlots];

    // Find the cover plane FIRST so we can preserve its video texture
    const slug = projectRouteUrl.split("/").pop();
    const coverPlane = planeId
      ? this.planes.find((p) => p.id === planeId) ?? null
      : this.planes.find((p) => p.id?.startsWith(`cover-${slug}`)) ?? null;
    const otherCovers = this.planes.filter((p) => p !== coverPlane);

    // Pause existing video textures — skip the cover to preserve continuity
    for (const plane of this.planes) {
      if (plane === coverPlane) continue;
      if (plane.videoTexture) {
        plane.videoTexture.pause();
        plane.videoTexture.onFrame(null);
        plane.videoTexture = null;
        plane.texture = null;
      }
    }

    console.debug("[scene] cover plane:", coverPlane?.id ?? planeId, coverPlane ? "found" : "missing");
    console.debug("[scene] other covers (→ offscreen):", otherCovers.length);

    // Push non-matching covers offscreen — they stay in the planes array
    // so restorePlanes() can bring them back, but they won't be visible.
    const OFF = -9999;
    for (const p of otherCovers) {
      p.boundsPx = { x: OFF, y: OFF, w: 0, h: 0, z: 0 };
      p.opacity = 0;
    }

    // Read project media from the loader
    const g = App.gpu as GPU | null;
    const media = (
      g?.media as Record<string, Record<string, RouteMediaSlot[]>> | undefined
    )?.[projectRouteUrl];
    const stack = media?.main;

    if (!Array.isArray(stack) || stack.length === 0) {
      console.debug("[scene] no media found for", projectRouteUrl);
      this.planes = coverPlane ? [coverPlane, ...otherCovers] : [...otherCovers];
      this.mediaSlots = [];
      this.collectDomSlots();
      this.updateBounds();
      return;
    }

    console.debug("[scene] project media slots:", stack.length);

    // Build new planes array:
    //   [active cover] + [project media 1..N] + [other covers offscreen]
    //
    // DOM .g__l order in the template:
    //   [0] = cover slot, [1..N] = media slots
    // So active planes [0..N] map to domSlots [0..N].
    // Other covers sit at the end with no DOM slot — they render at their
    // frozen offscreen boundsPx and stay invisible.

    const activePlanes: ScenePlane[] = [];
    const activeSlots: RouteMediaSlot[] = [];

    // Index 0: reuse the existing cover plane (same GPU texture = seamless)
    // Rename to match the project page's data-plane id so collectDomSlots maps correctly
    const projectCoverId = stack[0]?.id ?? null;
    if (coverPlane) {
      this._baseCoverId = coverPlane.id ?? null;
      if (projectCoverId) coverPlane.id = projectCoverId;
      activePlanes.push(coverPlane);
    } else {
      const slot0 = stack[0];
      if (slot0) {
        const fallback = this._createPlane(slot0);
        if (fallback) activePlanes.push(fallback);
      }
    }
    if (stack[0]) activeSlots.push(stack[0]);

    // Index 1+: create planes for the rest of the project media
    for (let i = 1; i < stack.length; i++) {
      const slot = stack[i];
      if (!slot) continue;
      const plane = this._createPlane(slot);
      if (plane) {
        activePlanes.push(plane);
      }
      activeSlots.push(slot);
    }

    // Combine: active planes first (bound to DOM), then offscreen covers
    this.planes = [...activePlanes, ...otherCovers];

    // mediaSlots only needs to cover the active range for initVideoPlanes
    // Pad with nulls for the offscreen covers so indices stay aligned
    this.mediaSlots = [
      ...activeSlots,
      ...otherCovers.map(() => null as unknown as RouteMediaSlot),
    ];

    // Rebind to DOM .g__l elements in the project template.
    // collectDomSlots finds N .g__l elements which map to activePlanes[0..N-1].
    // The trailing otherCovers have no DOM slot — domSlots[i] will be undefined,
    // so updateBounds skips them and their frozen offscreen bounds persist.
    this.collectDomSlots();

    // Reset and reinit video planes for the new DOM
    this.videoReady = new Promise<void>((resolve) => {
      this._resolveVideoReady = resolve;
    });
    this.initVideoPlanes();

    // Sync bounds (only active planes with DOM slots get updated)
    this.updateBounds();

    console.debug("[scene] injected — active:", activePlanes.length, "offscreen:", otherCovers.length, "domSlots:", this.domSlots.length);
    console.debug("[scene] planes after inject:", this.planes.map((p) => ({
      id: p.id,
      opacity: p.opacity,
      offscreen: p.boundsPx ? p.boundsPx.x === OFF : false,
      hasBounds: !!p.boundsPx,
    })));
  }

  /**
   * Restores the original cover planes after leaving a project page.
   *
   * Called by ProjectPage.out() before navigating back to /projects.
   */
  restorePlanes(): void {
    if (!this._basePlanes) return;

    console.debug("[scene] restorePlanes — back to covers");

    // Pause injected video textures (media planes + cover)
    for (const plane of this.planes) {
      if (plane.videoTexture) {
        plane.videoTexture.pause();
        plane.videoTexture.onFrame(null);
        plane.videoTexture = null;
        plane.texture = null;
      }
    }

    // Restore the cover plane's original ID (renamed by injectProjectMedia)
    if (this._baseCoverId) {
      const cover = this.planes.find((p) => p.id?.startsWith("cover-"));
      if (cover) cover.id = this._baseCoverId;
      this._baseCoverId = null;
    }

    // Restore the original cover planes and reset their visibility.
    this.planes = this._basePlanes;
    this.mediaSlots = this._baseMediaSlots ?? [];
    this._basePlanes = null;
    this._baseMediaSlots = null;

    for (const p of this.planes) {
      p.opacity = 1;
      p.boundsPx = null; // will be recomputed by updateBounds
    }

    // Rebind to projects grid DOM
    this.collectDomSlots();

    // Reinit videos for the restored planes
    this.videoReady = new Promise<void>((resolve) => {
      this._resolveVideoReady = resolve;
    });
    this.initVideoPlanes();

    this.updateBounds();

    console.debug("[scene] restored planes:", this.planes.length, this.planes.map((p) => p.id));
  }

  /**
   * Restores cover planes for a reverse transition (project → projects).
   *
   * Unlike restorePlanes(), this method:
   * - Preserves the cover plane's VideoTexture so initVideoPlanes can
   *   do a seamless handoff (no flash).
   * - Does NOT reset opacity or bounds — the page animation sets those.
   * - Re-collects DOM slots from the (now-present) projects grid DOM.
   *
   * @param slug - Project slug, used to identify the cover plane to preserve
   * @param planeId - Exact plane ID (preferred over slug for matching)
   */
  restoreForTransit(slug: string, planeId?: string): void {
    if (!this._basePlanes) return;

    // Destroy VTs for non-cover planes only.
    // The cover keeps its VT so initVideoPlanes enters the handoff
    // path (preserving texture + opacity) rather than hiding the plane.
    // The cover plane's ID was renamed by injectProjectMedia; match it
    // by its current ID (project-page convention) or its saved original
    const keepId = this._baseCoverId ? [this._baseCoverId, `cover-${slug}`] : planeId ? [planeId] : null;
    for (const plane of this.planes) {
      if (keepId ? keepId.includes(plane.id!) : plane.id?.startsWith(`cover-${slug}`)) continue;
      if (plane.videoTexture) {
        plane.videoTexture.pause();
        plane.videoTexture.onFrame(null);
        plane.videoTexture = null;
        plane.texture = null;
      }
    }

    // Restore the cover plane's original ID (renamed by injectProjectMedia)
    if (this._baseCoverId) {
      const cover = this.planes.find((p) => p.id?.startsWith("cover-"));
      if (cover) cover.id = this._baseCoverId;
      this._baseCoverId = null;
    }

    // Restore the original cover planes
    this.planes = this._basePlanes;
    this.mediaSlots = this._baseMediaSlots ?? [];
    this._basePlanes = null;
    this._baseMediaSlots = null;

    // Reset opacity and bounds for all restored planes — injectProjectMedia
    // set non-active covers to opacity 0 and pushed them offscreen.
    for (const p of this.planes) {
      p.opacity = 1;
      p.boundsPx = null;
    }

    // Re-collect DOM slots from the projects grid (correct DOM)
    this.collectDomSlots();

    // Reinit video planes — cover enters handoff path, others get new VTs
    this.videoReady = new Promise<void>((resolve) => {
      this._resolveVideoReady = resolve;
    });
    this.initVideoPlanes();
  }

  /**
   * Gets the plane index for a DOM element.
   *
   * @param el - DOM element to look up
   * @returns Index of the plane, or -1 if not found
   */
  getIndexForSlot(el: Element | null | undefined): number {
    if (!el) return -1;
    return this.domSlots.indexOf(el);
  }

  /**
   * Sets hover tilt angle for a plane.
   *
   * @param index - Plane index
   * @param value - Tilt angle in radians (positive = tilt forward)
   */
  setHoverTilt(index: number, value: number): void {
    const plane = this.planes[index];
    if (!plane) return;

    plane.hoverTilt = value;

    // Request redraw
    try {
      App.gpu?.requestExtraFrames?.();
    } catch {
      // Ignore errors
    }
  }

  /**
   * Sets hover zoom amount for a plane.
   *
   * @param index - Plane index
   * @param value - Zoom amount (0 = normal, 1 = fully zoomed)
   */
  setHoverZoom(index: number, value: number): void {
    const plane = this.planes[index];
    if (!plane) return;

    plane.hoverZoom = value;

    // Request redraw
    try {
      App.gpu?.requestExtraFrames?.();
    } catch {
      // Ignore errors
    }
  }

  /**
   * Resets all hover effects on all planes.
   *
   * Called when leaving a route to prevent effects from persisting
   * across route transitions.
   */
  resetHoverEffects(): void {
    for (let i = 0; i < this.planes.length; i += 1) {
      const p = this.planes[i];
      if (!p) continue;

      // Reset hover effect targets
      p.hoverZoom = 0;
      p.hoverTilt = 0;

      // Also zero eased values to prevent lingering animation
      p.hoverZoomEased = 0;
      p.hoverTiltEased = 0;
    }

    // Request redraw
    try {
      App.gpu?.requestExtraFrames?.();
    } catch {
      // Ignore errors
    }
  }

  /**
   * Updates plane bounds from DOM element positions.
   *
   * Called every frame (or throttled) to keep GL planes aligned with
   * their DOM slot elements.
   */
  updateBounds(): void {
    // Respect GPU bounds freeze — Transition.out() calls this directly,
    // which would snap animated plane positions back to DOM layout
    if ((App.gpu as GPU | null)?.boundsFrozen) return;

    for (let i = 0; i < this.planes.length; i++) {
      const el = this.domSlots[i];
      const plane = this.planes[i];
      if (!el || !plane) continue;

      // Get DOM bounds using kido utility
      const { left, top, width, height } = bounds(el);

      // Update plane bounds to match DOM element position
      plane.boundsPx = {
        x: left,
        y: top,
        w: width,
        h: height,
        z: 1, // Default z-index
      };
    }
  }

  /**
   * Builds a map of planes for transition animations.
   *
   * Used for coordinated transitions where specific planes need
   * to be animated differently (e.g., hero image morphing).
   *
   * @param id - Project identifier to match
   * @param mode - Transition mode
   * @returns Object with matched planes and others, or undefined
   */
  buildPlaneMap(
    id: string,
    mode: "to-case" | "to-home" | "to-project" | "to-projects"
  ): { map: ScenePlane[]; other: ScenePlane[] } | undefined {
    if (mode === "to-case") {
      // Find planes that match the project ID (cover or detail image)
      const map = this.planes.filter((p) => {
        return p.id?.startsWith(`cover-${id}`) || p.id === `img-${id}`;
      });

      // Get all other planes
      const other = this.planes.filter((p) => !map.includes(p));

      return { map, other };
    }

    if (mode === "to-home") {
      // Get planes from the destination (home) scene
      const to = this.gpu.transition?.toScene?.planes ?? [];
      const other = this.planes;

      // Find planes in destination that aren't in current
      const map = to.filter((p) => !other.find((o) => o.id === p.id));

      return { map, other };
    }

    if (mode === "to-project") {
      // Navigating from /projects grid into a specific project.
      // map = the cover plane that will animate to its new position
      // other = all remaining cover planes (will fade out)
      const map = this.planes.filter((p) => p.id?.startsWith(`cover-${id}`));
      const other = this.planes.filter((p) => !map.includes(p));
      return { map, other };
    }

    if (mode === "to-projects") {
      // Navigating back from project to /projects grid.
      // map = cover plane (index 0, will animate back to grid)
      // other = media planes (will fade out)
      const map = this.planes.filter((p) => p.id?.startsWith("cover-"));
      const other = this.planes.filter((p) => !map.includes(p));
      return { map, other };
    }

    return undefined;
  }
}
